import BaseTable from "#/components/tables/BaseTable";
import { useClient } from "#/hook/useClient";
import type { HawbDetail, HawbExitItemRow } from "#/types/manifest";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { Badge, Table } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface ExitItemsSectionProps {
  hawb: HawbDetail;
}

/**
 * Items of the last Validate exit. With an issue: only the packages concerned
 * (missing or untagged) with the reason. Without: every package read, with its
 * scan information.
 */
const ExitItemsSection = ({ hawb }: ExitItemsSectionProps) => {
  const { t } = useTranslation();
  const { client } = useClient();
  const navigate = useNavigate();
  const exit = hawb.last_exit;

  if (!exit) {
    return <span className="text-sm text-gray-500">{t("manifests.detail.notValidated")}</span>;
  }

  const isIssue = exit.verdict === "issue";
  const rows: HawbExitItemRow[] = exit.items.filter((row) =>
    isIssue ? row.result !== "read" : row.result === "read",
  );

  if (rows.length === 0) {
    return (
      <span className="text-sm text-gray-500">
        {isIssue ? t("manifests.detail.noItemsConcerned") : t("manifests.detail.noItemsScanned")}
      </span>
    );
  }

  const headers = isIssue
    ? [t("manifests.detail.columns.item"), t("manifests.detail.columns.rfid"), t("manifests.detail.columns.reason")]
    : [
        t("manifests.detail.columns.item"),
        t("manifests.detail.columns.rfid"),
        t("manifests.detail.columns.date"),
        t("manifests.detail.columns.time"),
        t("manifests.detail.columns.scanner"),
      ];

  return (
    <BaseTable
      headers={headers}
      rows={rows.map((row, index) => (
        <Table.Tr
          key={`${row.item_id ?? "deleted"}-${row.rfid ?? index}`}
          onClick={() => row.item_id && void navigate(`/inventory/items/${row.item_id}`)}
          className={row.item_id ? "cursor-pointer" : undefined}
        >
          <Table.Td>{row.external_id ?? (row.item_id ? `#${row.item_id}` : "--")}</Table.Td>
          <Table.Td>{row.rfid ?? t("manifests.detail.notTagged")}</Table.Td>
          {isIssue ? (
            <Table.Td>
              <Badge color="red" variant="light">
                {t(`manifests.detail.reason.${row.result}`)}
              </Badge>
            </Table.Td>
          ) : (
            <>
              <Table.Td>{formatDate(row.scanned_at, client)}</Table.Td>
              <Table.Td>{row.scanned_at ? formatTime(row.scanned_at, client) : "--"}</Table.Td>
              <Table.Td>{exit.device_name ?? "--"}</Table.Td>
            </>
          )}
        </Table.Tr>
      ))}
    />
  );
};

export default ExitItemsSection;
